import React, { useState } from 'react';
import { Column, Row } from 'react-foundation';
import * as cloudinaryAPI from '../../../../api/cloudinaryAPI';
import { PhotoViewer } from '../../shared/PhotoViewer';
import { StatusPopup } from '../../shared/StatusPopup';
import {
	getOrganization,
	postOrganizationAsset,
} from '../../../../api/organizationsAPI';
import LoadingSpinner from '../../shared/LoadingSpinner';

/**
 * Component for the gallery tab.
 * @param orgData the org data that is used
 * @param setOrgData setter for the org data
 * @param currentUserCanEdit whether the current user can edit or not (true for yes, false for no)
 * @param cloudinaryUploadWidget the cloudinary upload widget for files
 * @param maxGalleryFiles the max number of gallery objects allowed
 */
export const OrganizationGalleryTab = ({
	orgData,
	setOrgData,
	currentUserCanEdit,
	cloudinaryUploadWidget,
	maxGalleryFiles,
}) => {
	/**
	 * ---- Variables/States ----
	 * viewerShow: Responsible for showing and hiding the PhotoViewer component. True for show, false for hide.
	 * activeImageIndex: Keeps track of the current active image - this only comes into effect once an image is clicked, and more used in the PhotoViewer component
	 * statusPopupObject: Keeps track of the status popup banner component data
	 * showStatusPopupObject: Responsible for showing and hiding the status popup banner component. True for show, false for hide.
	 */
	const [viewerShow, setViewerShow] = useState(false);
	const [activeImageIndex, setActiveImageIndex] = useState(0);
	const [statusPopupObject, setStatusPopupObject] = useState({});
	const [showStatusPopupObject, setShowStatusPopupObject] = useState(false);
	const [isAssetUploading, setIsAssetUpload] = useState(false);

	const assetsLocal = JSON.parse(JSON.stringify(orgData?.assets));

	/**
	 * Handles clicking of an image, and sets the viewer to show with the selected image
	 * @param index the index of the image, correlated to the respective orgData.assets index
	 */
	const handleFileThumbnailClick = (index) => {
		setActiveImageIndex(index);
		setViewerShow(true);
	};

	/**
	 * Handles clicking of the upload button, and opens the cloudinary upload widget.
	 * If the maximum number of files allowed has been hit, then display an error message. Otherwise, open the widget.
	 */
	const openWidget = () => {
		if (orgData?.assets.length >= maxGalleryFiles) {
			setShowStatusPopupObject(false);
			let tempShowStatusObject = {
				type: 'failure',
				text:
					"You've hit the cap on the number of files allowed (" +
					maxGalleryFiles +
					'), please delete a file to upload more!',
			};
			setStatusPopupObject(tempShowStatusObject);
			setShowStatusPopupObject(true);
		} else {
			cloudinaryUploadWidget.open();
		}
	};

	const handleUploadImg = async () => {
		setIsAssetUpload(true);

		const { data: fetchedOrgData } = await getOrganization(orgData.id);
		const assetsToUpload = findDifferences(
			fetchedOrgData?.assets,
			orgData?.assets
		);

		assetsToUpload.forEach(async (asset) => {
			await postOrganizationAsset(orgData, asset);
		});
		setIsAssetUpload(false);
	};

	//util
	function findDifferences(input1, input2) {
		const output = [...input1];

		for (const obj2 of input2) {
			const obj1Index = output.findIndex((obj1) => obj1.id === obj2.id);
			if (obj1Index !== -1) {
				output.splice(obj1Index, 1);
			} else {
				output.push(obj2);
			}
		}

		return output;
	}

	const PhotoGallery = ({ header, gallaryData }) => {
		return (
			<Column>
				<div className="files-container">
					<h1>{header}</h1>
					<Row>
						{gallaryData.assets.map((file, index) => {
							let fileCase = cloudinaryAPI.checkFileType(file);

							return (
								<div key={index} className="image">
									{fileCase === 1 ? (
										<img
											src={file.secure_url}
											alt=""
											onClick={() =>
												handleFileThumbnailClick(index)
											}
										/>
									) : fileCase === 2 ? (
										<img
											src={cloudinaryAPI.formatCloudinaryThumbnailURLToBeFullSize(
												file.thumbnail_url
											)}
											alt=""
											onClick={() =>
												handleFileThumbnailClick(index)
											}
										/>
									) : fileCase === 3 ? (
										<video
											width="100%"
											onClick={() =>
												handleFileThumbnailClick(index)
											}
										>
											<source src={file.secure_url} />
										</video>
									) : (
										<div
											className="secondary-button"
											onClick={() =>
												handleFileThumbnailClick(index)
											}
										>
											<p>Unsupported File Format</p>
										</div>
									)}
								</div>
							);
						})}
					</Row>
				</div>
			</Column>
		);
	};

	if (isAssetUploading) {
		return <LoadingSpinner message={'Uploading...'} />;
	}

	return (
		<div className="gallery-tab">
			{showStatusPopupObject ? (
				<StatusPopup
					statusPopupObject={statusPopupObject}
					setShowStatusPopup={setShowStatusPopupObject}
				/>
			) : (
				''
			)}

			{viewerShow ? (
				<PhotoViewer
					data={orgData}
					setData={setOrgData}
					show={viewerShow}
					setShow={setViewerShow}
					startingIndex={activeImageIndex}
					currentUserCanEdit={currentUserCanEdit}
				/>
			) : (
				<></>
			)}

			<Row>
				<Column small={12}>
					{currentUserCanEdit && (
						<div>
							<p className="body-font">
								Showcase your events. You can upload your
								Instagram feed and supporting materials (e.g.
								pitchdecks, videos, promotional materials)!
							</p>
							<Column
								style={{
									display: 'flex',
									justifyContent: 'space-between',
								}}
							>
								<div
									id="upload_widget"
									className="primary-button"
									onClick={() => openWidget()}
								>
									<p>Upload</p>
								</div>
								<button
									id="upload_widget"
									className="primary-button"
									onClick={handleUploadImg}
									disabled={isAssetUploading}
								>
									<p>Publish</p>
								</button>
							</Column>
						</div>
					)}
				</Column>
			</Row>
			<PhotoGallery header={'Your Gallary'} gallaryData={orgData} />
		</div>
	);
};
